import { Button, Spinner } from '@heroui/react'

import { FormSection } from '@/components/ui/form-section'
import { AlertTriangleIcon, CheckCircleIcon } from '@/components/ui/icons'
import { errorMessage } from '@/lib/api/api-error'

import { useProductInventory } from '../hooks'
import { canSendToInventory } from '../product-inventory'
import type { ProductDetail } from '../types'

interface ProductInventorySectionProps {
  product: ProductDetail
  isDisabled?: boolean
}

/**
 * Whether the product reached inventory, and a Retry when it did not.
 *
 * Saving a product tells inventory about it in passing, and a save never fails
 * because inventory is down: the product is written whatever happens to the
 * message. So a product can exist here and not there — unsellable through
 * recipes, uncounted — with nothing on screen to say so. This says so.
 */
export function ProductInventorySection({ product, isDisabled }: ProductInventorySectionProps) {
  const { rows, missing, isChecking, isAwaiting, error, check, retry } =
    useProductInventory(product)

  const sendable = missing.filter((row) => canSendToInventory(row.ref))
  const isVariants = rows.length > 1

  return (
    <FormSection title="Inventory" description="Whether stock is tracked for this product.">
      {isChecking ? (
        <p className="text-muted flex items-center gap-2 text-xs">
          <Spinner size="sm" />
          Checking inventory…
        </p>
      ) : error ? (
        /* A failed check must not read as "not in inventory" — Retry would
           resend a product inventory may well have. */
        <div className="flex flex-col items-start gap-2">
          <p className="text-danger text-xs" role="alert">
            {errorMessage(error, 'Could not check inventory. Please try again.')}
          </p>
          <Button size="sm" variant="ghost" onPress={check}>
            Check again
          </Button>
        </div>
      ) : missing.length === 0 ? (
        <p className="text-success flex items-center gap-2 text-xs">
          <CheckCircleIcon width={14} height={14} />
          {isVariants ? `All ${rows.length} variants are tracked.` : 'Tracked in inventory.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-warning flex items-start gap-2 text-xs leading-relaxed">
            <AlertTriangleIcon width={14} height={14} className="mt-0.5 shrink-0" />
            {isVariants
              ? `${missing.length} of ${rows.length} variants are not in inventory.`
              : 'Not in inventory. Its stock is not tracked.'}
          </p>

          {isVariants && (
            <ul className="text-muted flex flex-col gap-1 text-xs">
              {missing.map(({ ref }) => (
                <li key={ref.id}>
                  {ref.label}
                  {!canSendToInventory(ref) && ' — needs a SKU first'}
                </li>
              ))}
            </ul>
          )}

          {!isVariants && sendable.length === 0 && (
            <p className="text-muted text-xs">Give it a SKU and save, then retry.</p>
          )}

          {isAwaiting ? (
            <p className="text-muted flex items-center gap-2 text-xs">
              <Spinner size="sm" />
              Sent. Waiting for inventory to pick it up…
            </p>
          ) : (
            retry.isSuccess && (
              /* Inventory refuses an item whose SKU another one already holds,
                 and a resend cannot get round that — only a new SKU can. */
              <p className="text-muted text-xs leading-relaxed">
                Inventory has not picked it up yet. If it still does not appear, check that no
                other stock item uses the same SKU.
              </p>
            )
          )}

          {retry.error && (
            <p className="text-danger text-xs" role="alert">
              {errorMessage(retry.error, 'Could not send it to inventory. Please try again.')}
            </p>
          )}

          <div>
            <Button
              size="sm"
              variant="secondary"
              onPress={() => retry.mutate()}
              isDisabled={isDisabled || retry.isPending || isAwaiting || sendable.length === 0}
            >
              {retry.isPending && <Spinner size="sm" />}
              {retry.isPending ? 'Sending…' : 'Retry'}
            </Button>
          </div>
        </div>
      )}
    </FormSection>
  )
}
